/**
 * Geometry Processing Redux Slice
 *
 * Manages state for geometry processing tools including dissolve,
 * simplify, and geometry calculator operations.
 *
 * **Validates: Requirements 50.1, 51.1, 52.1**
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  // AggregationOperation, // unused - removed for clean build / robustness
  GeometricProperty,
  LengthUnit,
  AreaUnit,
  FieldAggregation,
} from '../../services/GeometryProcessor';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type GeometryProcessingTool = 'dissolve' | 'simplify' | 'calculator' | null;

export interface DissolveToolState {
  sourceLayerId: string | null;
  dissolveField: string | null;
  aggregations: FieldAggregation[];
}

export interface SimplifyToolState {
  sourceLayerId: string | null;
  tolerance: number;
  highQuality: boolean;
}

export interface CalculatorToolState {
  sourceLayerId: string | null;
  selectedProperties: GeometricProperty[];
  lengthUnit: LengthUnit;
  areaUnit: AreaUnit;
  autoUpdate: boolean;
}

export interface GeometryProcessingState {
  activeTool: GeometryProcessingTool;
  isProcessing: boolean;
  error: string | null;
  message: string | null;

  // Tool-specific state
  dissolveTool: DissolveToolState;
  simplifyTool: SimplifyToolState;
  calculatorTool: CalculatorToolState;

  // Layers with auto-update enabled for geometry calculator
  autoUpdateLayerIds: string[];
}

// ---------------------------------------------------------------------------
// Initial State
// ---------------------------------------------------------------------------

const initialState: GeometryProcessingState = {
  activeTool: null,
  isProcessing: false,
  error: null,
  message: null,

  dissolveTool: {
    sourceLayerId: null,
    dissolveField: null,
    aggregations: [],
  },

  simplifyTool: {
    sourceLayerId: null,
    tolerance: 0.001,
    highQuality: true,
  },

  calculatorTool: {
    sourceLayerId: null,
    selectedProperties: [],
    lengthUnit: 'meters',
    areaUnit: 'square-meters',
    autoUpdate: false,
  },

  autoUpdateLayerIds: [],
};

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const geometryProcessingSlice = createSlice({
  name: 'geometryProcessing',
  initialState,
  reducers: {
    // Tool activation
    setActiveTool: (state, action: PayloadAction<GeometryProcessingTool>) => {
      state.activeTool = action.payload;
      state.error = null;
      state.message = null;
    },

    closeTool: (state) => {
      state.activeTool = null;
      state.error = null;
      state.message = null;
    },

    // Processing state
    setProcessing: (state, action: PayloadAction<boolean>) => {
      state.isProcessing = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isProcessing = false;
    },

    setMessage: (state, action: PayloadAction<string | null>) => {
      state.message = action.payload;
    },

    // Dissolve tool
    setDissolveSourceLayer: (state, action: PayloadAction<string | null>) => {
      state.dissolveTool.sourceLayerId = action.payload;
      state.dissolveTool.dissolveField = null;
      state.dissolveTool.aggregations = [];
    },

    setDissolveField: (state, action: PayloadAction<string | null>) => {
      state.dissolveTool.dissolveField = action.payload;
    },

    addDissolveAggregation: (state, action: PayloadAction<FieldAggregation>) => {
      const existing = state.dissolveTool.aggregations.findIndex(
        (a) => a.field === action.payload.field
      );
      if (existing >= 0) {
        state.dissolveTool.aggregations[existing] = action.payload;
      } else {
        state.dissolveTool.aggregations.push(action.payload);
      }
    },

    removeDissolveAggregation: (state, action: PayloadAction<string>) => {
      state.dissolveTool.aggregations = state.dissolveTool.aggregations.filter(
        (a) => a.field !== action.payload
      );
    },

    clearDissolveAggregations: (state) => {
      state.dissolveTool.aggregations = [];
    },

    // Simplify tool
    setSimplifySourceLayer: (state, action: PayloadAction<string | null>) => {
      state.simplifyTool.sourceLayerId = action.payload;
    },

    setSimplifyTolerance: (state, action: PayloadAction<number>) => {
      state.simplifyTool.tolerance = action.payload;
    },

    setSimplifyHighQuality: (state, action: PayloadAction<boolean>) => {
      state.simplifyTool.highQuality = action.payload;
    },

    // Calculator tool
    setCalculatorSourceLayer: (state, action: PayloadAction<string | null>) => {
      state.calculatorTool.sourceLayerId = action.payload;
    },

    toggleCalculatorProperty: (state, action: PayloadAction<GeometricProperty>) => {
      const idx = state.calculatorTool.selectedProperties.indexOf(action.payload);
      if (idx >= 0) {
        state.calculatorTool.selectedProperties.splice(idx, 1);
      } else {
        state.calculatorTool.selectedProperties.push(action.payload);
      }
    },

    setCalculatorProperties: (state, action: PayloadAction<GeometricProperty[]>) => {
      state.calculatorTool.selectedProperties = action.payload;
    },

    setCalculatorLengthUnit: (state, action: PayloadAction<LengthUnit>) => {
      state.calculatorTool.lengthUnit = action.payload;
    },

    setCalculatorAreaUnit: (state, action: PayloadAction<AreaUnit>) => {
      state.calculatorTool.areaUnit = action.payload;
    },

    setCalculatorAutoUpdate: (state, action: PayloadAction<boolean>) => {
      state.calculatorTool.autoUpdate = action.payload;
    },

    // Auto-update layer tracking
    addAutoUpdateLayer: (state, action: PayloadAction<string>) => {
      if (!state.autoUpdateLayerIds.includes(action.payload)) {
        state.autoUpdateLayerIds.push(action.payload);
      }
    },

    removeAutoUpdateLayer: (state, action: PayloadAction<string>) => {
      state.autoUpdateLayerIds = state.autoUpdateLayerIds.filter((id) => id !== action.payload);
    },
  },
});

export const {
  setActiveTool,
  closeTool,
  setProcessing,
  setError,
  setMessage,
  setDissolveSourceLayer,
  setDissolveField,
  addDissolveAggregation,
  removeDissolveAggregation,
  clearDissolveAggregations,
  setSimplifySourceLayer,
  setSimplifyTolerance,
  setSimplifyHighQuality,
  setCalculatorSourceLayer,
  toggleCalculatorProperty,
  setCalculatorProperties,
  setCalculatorLengthUnit,
  setCalculatorAreaUnit,
  setCalculatorAutoUpdate,
  addAutoUpdateLayer,
  removeAutoUpdateLayer,
} = geometryProcessingSlice.actions;

export default geometryProcessingSlice.reducer;
